import styled, { keyframes } from 'styled-components';

/* ------------------------------------------------------------------ */
/* Page shell — matches the Dashboard page's wrapper/container pair    */
/* ------------------------------------------------------------------ */

export const TicketsWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
  box-sizing: border-box;
  overflow-x: hidden;
`;

export const TicketsContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ------------------------------------------------------------------ */
/* Page header                                                         */
/* ------------------------------------------------------------------ */

export const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.md};
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-wrap: wrap;
`;

export const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 1.2rem;
  }
`;

export const TotalBadge = styled.span`
  padding: 4px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.full};
  background: ${({ theme }) => theme.landing.iconBg};
  color: ${({ theme }) => theme.landing.textSecondary};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 11px;
  font-weight: 600;
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;

export const Description = styled.p`
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 4px 0 0 0;
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    display: none;
  }
`;

export const RaiseTicketButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  height: 36px;
  padding: 0 ${({ theme }) => theme.landingSpacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.15s ease,
    transform 0.1s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: ${({ theme }) => theme.landing.buttonHover};
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    justify-content: center;
  }
`;

/* ------------------------------------------------------------------ */
/* Toolbar card wrapping the reusable SearchBar + FilterBar            */
/* ------------------------------------------------------------------ */

export const ToolbarCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.landingSpacing.md};
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.md};
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

export const ToolbarRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
  }
`;

/* ------------------------------------------------------------------ */
/* Table card                                                           */
/* ------------------------------------------------------------------ */

export const TableCard = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
`;

/* ------------------------------------------------------------------ */
/* Ticket ID cell + unviewed indicator                                  */
/* ------------------------------------------------------------------ */

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  75%, 100% { transform: scale(1.8); opacity: 0; }
`;

export const TicketIdCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

/* Fixed-width slot, rendered on every row so ticket IDs stay aligned */
export const UnviewedSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
`;

export const UnviewedDot = styled.span`
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.statusActive};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${({ theme }) => theme.colors.statusActive};
    animation: ${ping} 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
      opacity: 0;
    }
  }
`;

export const UnviewedCount = styled.span`
  position: relative;
  color: #ffffff;
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
`;
